/**
 * Date utilities.
 *
 * IMPORTANT:
 * - Strings no formato "YYYY-MM-DD" (vindas do backend como `date`) NÃO devem ser parseadas via `new Date(str)`
 *   porque o JS interpreta como UTC e pode exibir D-1 em fusos negativos (ex.: Brasil).
 * - Para datas "date-only", criamos `new Date(ano, mes-1, dia)` (timezone local) para evitar o shift.
 */

export const isIsoDateOnly = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value);

export const parseDateStringToLocalDate = (value: string): Date | null => {
  if (!value) return null;

  if (isIsoDateOnly(value)) {
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    const d = new Date(year, month - 1, day);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDateShortPtBR = (value?: string): string | null => {
  if (!value) return null;
  const d = parseDateStringToLocalDate(value);
  if (!d) return null;
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
};

export const formatDatePtBR = (value?: string): string | null => {
  if (!value) return null;
  const d = parseDateStringToLocalDate(value);
  if (!d) return null;
  return d.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const getValidDate = (year: number, monthIndex: number, day: number): Date => {
  const candidate = new Date(year, monthIndex, day);
  if (candidate.getMonth() !== monthIndex) {
    return new Date(year, monthIndex + 1, 0);
  }
  return candidate;
};

const getPeriodBoundsForDate = (paymentDay: number, referenceDate: Date) => {
  const year = referenceDate.getFullYear();
  const monthIndex = referenceDate.getMonth();
  const currentPaymentDate = getValidDate(year, monthIndex, paymentDay);

  if (referenceDate >= currentPaymentDate) {
    const nextMonth = new Date(year, monthIndex + 1, 1);
    const nextPaymentDate = getValidDate(nextMonth.getFullYear(), nextMonth.getMonth(), paymentDay);
    const end = new Date(nextPaymentDate);
    end.setDate(end.getDate() - 1);
    return { start: currentPaymentDate, end };
  }

  const prevMonth = new Date(year, monthIndex - 1, 1);
  const start = getValidDate(prevMonth.getFullYear(), prevMonth.getMonth(), paymentDay);
  const end = new Date(currentPaymentDate);
  end.setDate(end.getDate() - 1);
  return { start, end };
};

export const getBudgetPeriodPermissions = ({
  startDate,
  endDate,
  paymentDay,
  referenceDate = new Date(),
}: {
  startDate?: string;
  endDate?: string;
  paymentDay?: number;
  referenceDate?: Date;
}) => {
  const start = startDate ? parseDateStringToLocalDate(startDate) : null;
  const end = endDate ? parseDateStringToLocalDate(endDate) : null;
  const referenceDay = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );

  const isCurrent = !!start && !!end && referenceDay >= start && referenceDay <= end;
  const isFuture = !!start && referenceDay < start;
  const isPast = !!end && referenceDay > end;

  let isImmediatePrevious = false;
  if (paymentDay && end) {
    const bounds = getPeriodBoundsForDate(paymentDay, referenceDay);
    const expectedPrevEnd = new Date(bounds.start);
    expectedPrevEnd.setDate(expectedPrevEnd.getDate() - 1);
    isImmediatePrevious = isSameDay(end, expectedPrevEnd);
  }

  const canPlan = isCurrent || isFuture || isImmediatePrevious;
  const canRealize = isCurrent || isImmediatePrevious;

  return {
    isCurrent,
    isFuture,
    isPast,
    isImmediatePrevious,
    canPlan,
    canRealize,
  };
};

const toIsoDateOnly = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Verifica se uma data "date-only" (yyyy-MM-dd) pertence ao intervalo
 * [start, end] de um período orçamentário (limites inclusivos).
 * Sem período definido não há restrição (retorna true).
 */
export const isDateWithinPeriod = (
  dateStr?: string,
  startStr?: string,
  endStr?: string
): boolean => {
  const date = dateStr ? parseDateStringToLocalDate(dateStr) : null;
  if (!date) return false;

  const start = startStr ? parseDateStringToLocalDate(startStr) : null;
  const end = endStr ? parseDateStringToLocalDate(endStr) : null;
  if (!start || !end) return true;

  return date >= start && date <= end;
};

/**
 * Data padrão (yyyy-MM-dd) para realizar/receber dentro de um período.
 *
 * Usa "hoje" quando hoje pertence ao período; caso contrário faz clamp para o
 * limite do período (fim quando o período já se encerrou, início quando ainda
 * não começou). Assim o pré-preenchimento nunca cai fora do período do
 * lançamento — evita, por exemplo, sugerir a data de hoje num PO já encerrado.
 */
export const getDefaultRealizationDate = (
  startStr?: string,
  endStr?: string,
  referenceDate: Date = new Date()
): string => {
  const today = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );

  const start = startStr ? parseDateStringToLocalDate(startStr) : null;
  const end = endStr ? parseDateStringToLocalDate(endStr) : null;

  if (start && today < start) return toIsoDateOnly(start);
  if (end && today > end) return toIsoDateOnly(end);
  return toIsoDateOnly(today);
};

export const getInclusiveDaysRemaining = (
  endDate?: string,
  referenceDate: Date = new Date()
): number => {
  if (!endDate) return 0;

  const end = parseDateStringToLocalDate(endDate);
  if (!end) return 0;

  const referenceDay = new Date(
    referenceDate.getFullYear(),
    referenceDate.getMonth(),
    referenceDate.getDate()
  );

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffDays = Math.round((end.getTime() - referenceDay.getTime()) / msPerDay) + 1;
  return Math.max(0, diffDays);
};



